import {useEffect, useMemo, useRef, useState} from 'react';
import {useBookmarkAheadFeed} from './useBookmarkAheadFeed';

// Some card components read `start_date_time`/`end_date_time`; offers use `starts_at`/`ends_at`.
// Normalize so either path works.
const transformArticle = (article: any) => ({
  ...article,
  start_date_time: article.start_date_time || article.starts_at,
  end_date_time: article.end_date_time || article.ends_at,
});

export function useBookmarkFeedState(params?: {latitude?: number; longitude?: number}) {
  const [hyperLocal, setHyperLocal] = useState<any[]>([]);
  const [systemPopular, setSystemPopular] = useState<any[]>([]);
  const [generalUpcoming, setGeneralUpcoming] = useState<any[]>([]);
  const [bookmarkedUpcoming, setBookmarkedUpcoming] = useState<any[]>([]);
  // bannerRef = the sticky "N things saved" banner; its height drives sticky-offset math.
  // bookmarkedUpcomingSectionRef = target of scrollIntoView() when the banner is tapped.
  const bannerRef = useRef<HTMLDivElement>(null);
  const bookmarkedUpcomingSectionRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  // `loading` = first-load only (no cached data yet); `refetching` = any in-flight fetch
  // (including post-invalidate refetches when the location changes). Page surfaces both:
  // a full-screen spinner on first load, a subtle indicator on the section during refetches.
  const {
    data: feedData,
    isLoading: loading,
    isFetching: refetching,
  } = useBookmarkAheadFeed(true, params);

  // Single source of truth for the banner count and the BookmarkAhead exit gate.
  // Optimistically adjusted (±1) at tap time by useBookmarkActions, then reconciled with the
  // server's value on the next refetch (triggered by invalidateQueries on toggle success).
  const apiBookmarkedUpcomingCount =
    feedData?.data?.meta?.bookmarked_upcoming_count || 0;

  // Measure the sticky header (location picker + saved-items banner) so section headers
  // can offset by its height — they stick below it. offsetHeight (border-box) is required
  // because the banner has padding; entry.contentRect.height returns the content-box and
  // leaves section headers pinning ~24px too high, sliding behind the banner.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) {
      setBannerHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => {
      setBannerHeight(Math.floor(el.offsetHeight));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!feedData?.data?.data) return;

    const data = feedData.data.data;
    const hyper = (data.hyper_local || []).map(transformArticle);
    const popular = (data.system_popular || []).map(transformArticle);
    const upcoming = (data.general_upcoming || []).map(transformArticle);
    const saved = (data.bookmarked_upcoming || []).map(transformArticle);

    setHyperLocal(hyper);
    setSystemPopular(popular);
    setGeneralUpcoming(upcoming);
    setBookmarkedUpcoming(saved);
  }, [feedData]);

  // Single source of truth for which article IDs are currently bookmarked: derived from
  // is_bookmarked flags maintained optimistically by useBookmarkActions on the feed cache.
  // Avoids a parallel Set that has to be rolled back on API failures.
  const bookmarkedIds = useMemo(() => {
    const out = new Set<string>();
    [...hyperLocal, ...systemPopular, ...generalUpcoming, ...bookmarkedUpcoming].forEach(
      (article: any) => {
        if (article.is_bookmarked) out.add(article.id.toString());
      },
    );
    return out;
  }, [hyperLocal, systemPopular, generalUpcoming, bookmarkedUpcoming]);

  return {
    hyperLocal,
    systemPopular,
    generalUpcoming,
    bookmarkedUpcoming,
    bookmarkedIds,
    feedData,
    loading,
    bannerRef,
    bookmarkedUpcomingSectionRef,
    bannerHeight,
    apiBookmarkedUpcomingCount,
    refetching,
  };
}
